<template>
  <teleport to="body">
    <v-navigation-drawer v-model="drawerOpen" location="right" temporary width="480">
      <div class="pa-5 d-flex flex-column ga-4">
        <div class="d-flex align-start justify-space-between ga-3">
          <div>
            <div class="text-caption text-medium-emphasis">{{ $t(`${copyKey}.eyebrow`) }}</div>
            <h3 class="text-h6">{{ $t(`${copyKey}.title`) }}</h3>
            <p class="text-body-2 text-medium-emphasis mb-0">{{ $t(`${copyKey}.hint`) }}</p>
          </div>
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="drawerOpen = false" />
        </div>

        <v-alert v-if="link" type="info" density="comfortable">
          {{ $t(`${copyKey}.subject`, { value: link.value_display }) }}
        </v-alert>

        <v-alert v-if="errorMessage" type="error" closable @click:close="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>

        <v-select
          v-model="form.classification"
          :items="classificationOptions"
          item-title="title"
          item-value="value"
          :label="$t(`${copyKey}.classification`)"
          :hint="classificationHint"
          persistent-hint
          :disabled="saving"
          @update:model-value="handleClassificationChange"
        />

        <template v-if="requiresPerson">
          <v-autocomplete
            v-model="form.linkedPersonId"
            v-model:search="personSearch"
            :items="personOptions"
            item-title="title"
            item-value="value"
            :label="$t(`${copyKey}.person`)"
            :no-data-text="personsLoading ? $t(`${copyKey}.personLoading`) : $t(`${copyKey}.personEmpty`)"
            :loading="personsLoading"
            :disabled="saving"
            hide-no-data="false"
            no-filter
          />
          <p class="text-caption text-medium-emphasis mb-0">{{ $t(`${copyKey}.personScopeHint`) }}</p>
          <p v-if="personsHasMore" class="text-caption text-medium-emphasis mb-0">
            {{ $t(`${copyKey}.personMore`) }}
          </p>
        </template>

        <v-alert type="warning" density="comfortable">
          {{ $t(`${copyKey}.noTransferNotice`) }}
        </v-alert>

        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" :disabled="saving" @click="drawerOpen = false">
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!canSubmit" @click="submit">
            {{ $t(`${copyKey}.save`) }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>
  </teleport>
</template>

<script>
import axios from "axios";

const CLASSIFICATIONS = ["corporate_generic", "personal_named", "linked_to_person", "unknown"];
const PERSON_PAGE_SIZE = 25;

export default {
  name: "NcrmPrivacyContactPointClassificationDrawer",
  props: {
    modelValue: { type: Boolean, default: false },
    companyId: { type: [String, Number], required: true },
    link: { type: Object, default: null },
  },
  emits: ["update:modelValue", "saved"],
  data: () => ({
    saving: false,
    errorMessage: "",
    persons: [],
    personsLoading: false,
    personsHasMore: false,
    personSearch: "",
    personSearchDebounceId: null,
    personsSequence: 0,
    mutationSequence: 0,
    form: { classification: "unknown", linkedPersonId: null },
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.record.contactPoints.classify"; },
    drawerOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    requiresPerson() { return this.form.classification === "linked_to_person"; },
    classificationOptions() {
      return CLASSIFICATIONS.map((value) => ({
        value,
        title: this.$t(`ncrm.contacts.dataProtection.record.contactPoints.classifications.${value}`),
      }));
    },
    classificationHint() {
      return this.$t(
        `ncrm.contacts.dataProtection.record.contactPoints.classificationHints.${this.form.classification}`
      );
    },
    personOptions() {
      const options = this.persons.map((person) => ({
        value: Number(person.id),
        title: [person.first_name, person.last_name].filter(Boolean).join(" ")
          || this.$t(`${this.copyKey}.personFallback`, { id: person.id }),
      }));
      // Persoana deja selectată rămâne vizibilă chiar dacă o căutare nouă nu o
      // mai conține; altfel autocomplete-ul ar afișa o selecție goală.
      if (this.form.linkedPersonId && !options.some(({ value }) => value === this.form.linkedPersonId)) {
        options.unshift({
          value: this.form.linkedPersonId,
          title: this.selectedPersonLabel
            || this.$t(`${this.copyKey}.personFallback`, { id: this.form.linkedPersonId }),
        });
      }
      return options;
    },
    selectedPersonLabel() {
      const person = this.link?.linked_person;
      if (!person || Number(person.id) !== Number(this.form.linkedPersonId)) return "";
      return [person.first_name, person.last_name].filter(Boolean).join(" ");
    },
    canSubmit() {
      if (this.saving || !this.link) return false;
      return this.requiresPerson ? Boolean(this.form.linkedPersonId) : true;
    },
  },
  watch: {
    modelValue(value) {
      if (value) this.resetForm();
      else {
        this.cancelPersonSearch();
        this.invalidatePendingMutation();
      }
    },
    // Schimbarea recordului închide drawer-ul și golește selecția: o clasificare
    // pregătită pentru o companie nu are voie să se aplice alteia.
    companyId() {
      if (this.modelValue) this.drawerOpen = false;
      this.resetForm();
    },
    personSearch() {
      if (!this.modelValue || !this.requiresPerson) return;
      this.cancelPersonSearch();
      this.personSearchDebounceId = setTimeout(() => this.loadPersons(), 300);
    },
  },
  beforeUnmount() {
    this.cancelPersonSearch();
    this.invalidatePendingMutation();
  },
  methods: {
    invalidatePendingMutation() {
      this.mutationSequence += 1;
      this.saving = false;
    },
    resetForm() {
      // Invalidează și cererile deja trimise, nu doar debounce-ul. Altfel un
      // răspuns pentru compania precedentă poate repopula selectorul după ce
      // recordul s-a schimbat.
      this.personsSequence += 1;
      this.invalidatePendingMutation();
      this.personsLoading = false;
      this.cancelPersonSearch();
      this.errorMessage = "";
      this.persons = [];
      this.personsHasMore = false;
      this.personSearch = "";
      this.form = {
        classification: this.link?.classification || "unknown",
        linkedPersonId: this.link?.linked_person?.id ? Number(this.link.linked_person.id) : null,
      };
      if (this.requiresPerson) this.loadPersons();
    },
    cancelPersonSearch() {
      if (!this.personSearchDebounceId) return;
      clearTimeout(this.personSearchDebounceId);
      this.personSearchDebounceId = null;
    },
    handleClassificationChange() {
      if (!this.requiresPerson) {
        this.form.linkedPersonId = null;
        return;
      }
      if (!this.persons.length) this.loadPersons();
    },
    async loadPersons() {
      if (!this.companyId) return;
      const sequence = ++this.personsSequence;
      this.personsLoading = true;
      try {
        const { data } = await axios.get(
          `/ncrm/privacy/subjects/company/${this.companyId}/linkable-persons`,
          { params: { search: this.personSearch || undefined, limit: PERSON_PAGE_SIZE } }
        );
        if (sequence !== this.personsSequence) return;
        this.persons = Array.isArray(data?.items) ? data.items : [];
        this.personsHasMore = Boolean(data?.pagination?.has_more);
      } catch {
        if (sequence !== this.personsSequence) return;
        this.persons = [];
        this.personsHasMore = false;
        this.errorMessage = this.$t(`${this.copyKey}.errors.personsLoadError`);
      } finally {
        if (sequence === this.personsSequence) this.personsLoading = false;
      }
    },
    errorKey(error) {
      const code = error?.response?.data?.code;
      return [
        "privacy_linked_person_required",
        "privacy_linked_person_not_allowed",
        "privacy_not_found",
      ].includes(code) ? code : "submitError";
    },
    async submit() {
      if (!this.canSubmit) return;
      const sequence = ++this.mutationSequence;
      const originCompanyId = String(this.companyId);
      const originLinkId = String(this.link.link_id);
      this.saving = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.patch(
          `/ncrm/privacy/subjects/company/${originCompanyId}/contact-point-links/${originLinkId}`,
          {
            classification: this.form.classification,
            linked_person_id: this.requiresPerson ? this.form.linkedPersonId : null,
          }
        );
        if (sequence !== this.mutationSequence) return;
        this.$emit("saved", { data, companyId: originCompanyId });
        this.$emit("update:modelValue", false);
      } catch (error) {
        if (sequence !== this.mutationSequence) return;
        // Eroarea nu închide formularul și nu pierde ce a introdus utilizatorul.
        this.errorMessage = this.$t(`${this.copyKey}.errors.${this.errorKey(error)}`);
      } finally {
        if (sequence === this.mutationSequence) this.saving = false;
      }
    },
  },
};
</script>
